import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import {
  hpiSeriesId,
  isStale,
  momentumNarrative,
  parseFredCsv,
  permitsSeriesId,
  summarizeHpi,
  summarizePermits,
  type HpiSummary,
  type PermitsSummary,
} from "@/lib/housing-momentum";

/**
 * State housing momentum: FHFA House Price Index + Census Building Permits
 * Survey, both retrieved as public FRED series.
 *
 * Failure is explicit — a series that does not return observations comes back
 * as `null` and the panel renders an unavailable state rather than a
 * substituted or estimated figure.
 */

export type HousingMomentum = {
  stateAbbrev: string;
  stateName: string;
  hpi: HpiSummary | null;
  permits: PermitsSummary | null;
  hpiStale: boolean;
  permitsStale: boolean;
  narrative: string;
  sources: Array<{ label: string; href: string }>;
  fetchedAt: string;
};

const CACHE_MS = 1000 * 60 * 60 * 12;
const cache = new Map<string, { at: number; value: HousingMomentum }>();

async function fetchSeries(seriesId: string) {
  try {
    const res = await fetch(`https://fred.stlouisfed.org/graph/fredgraph.csv?id=${seriesId}`, {
      headers: { accept: "text/csv" },
      signal: AbortSignal.timeout(8_000),
    });
    if (!res.ok) return [];
    return parseFredCsv(await res.text());
  } catch (e) {
    console.error(`housing-momentum ${seriesId} failed:`, (e as Error).message);
    return [];
  }
}

const schema = z.object({
  stateAbbrev: z.string().length(2),
  stateName: z.string().min(2).max(60),
});

export const getHousingMomentum = createServerFn({ method: "GET" })
  .inputValidator((d) => schema.parse(d))
  .handler(async ({ data }): Promise<HousingMomentum> => {
    const key = data.stateAbbrev.toUpperCase();
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.value;

    const [hpiObs, permitObs] = await Promise.all([
      fetchSeries(hpiSeriesId(key)),
      fetchSeries(permitsSeriesId(key)),
    ]);
    const hpi = summarizeHpi(hpiObs);
    const permits = summarizePermits(permitObs);

    const value: HousingMomentum = {
      stateAbbrev: key,
      stateName: data.stateName,
      hpi,
      permits,
      hpiStale: hpi ? isStale(hpi.latest.date) : false,
      permitsStale: permits ? isStale(permits.latest.date) : false,
      narrative: momentumNarrative(data.stateName, hpi, permits),
      sources: [
        {
          label: "FHFA House Price Index",
          href: "https://www.fhfa.gov/data/hpi",
        },
        {
          label: "Census Building Permits Survey",
          href: "https://www.census.gov/construction/bps/",
        },
        { label: "Series delivery via FRED", href: "https://fred.stlouisfed.org/" },
      ],
      fetchedAt: new Date().toISOString(),
    };

    if (hpi || permits) cache.set(key, { at: Date.now(), value });
    return value;
  });
